import { ReactElement, useCallback } from 'react';
import { Pagination, PetitionsHistoryResponse } from '../types';
import { getPetitionsHistory } from '../../../api/petitionsHistoryRequests';
import { createFetchContext } from '../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../components/Info/DisplayError';
import Loading from '../../../../../../components/Info/Loading';

export interface PetitionsHistoryProps<T> {
  studentId: number;
  pagination: Pagination;
  children: (
    props: { data: T },
    query: {
      studentId: number;
      pagination: Pagination;
    },
    updateQuery: (
      query?:
        | {
            studentId: number;
            pagination: Pagination;
          }
        | undefined,
    ) => void,
  ) => ReactElement;
}

const { FetchProvider, FetchConsumer } = createFetchContext<
  {
    studentId: number;
    pagination: Pagination;
  },
  PetitionsHistoryResponse
>();

export const PetitionsHistoryLoaderConsumer = FetchConsumer;

export function PetitionsHistoryLoader({
  studentId,
  pagination,
  children,
}: PetitionsHistoryProps<PetitionsHistoryResponse>) {
  const request = useCallback(() => {
    return getPetitionsHistory(studentId, pagination);
  }, [studentId, pagination]);
  return (
    <FetchProvider
      request={request}
      defaultQuery={{ pagination, studentId }}
      fetchImmediately
    >
      <FetchConsumer>
        {({ data, loading, error, refresh, query, updateQuery }) => {
          if (error) {
            return (
              <DisplayError
                insideCard
                textBody={error.code}
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          if (loading) {
            return (
              <div className="mx-3">
                <Loading insideCard />
              </div>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }

          return children({ data }, query, updateQuery);
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
